import { Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, Query } from '@angular/fire/compat/firestore';
import { firstValueFrom, map, Observable } from 'rxjs';
import { QueryParam } from './../../models/query-parameter.model';
import { OrderBy } from './../../models/order-by.model';
import { getAttributeByPath } from './../../functions/attribute-by-path.function';
import { getResultValues } from './../../functions/get-result-value.function';
import { getCountFromServer, collection as coll, query, where } from '@angular/fire/firestore';
import { LoggerService } from '../logger.service';
import { AngularFireStorage } from '@angular/fire/compat/storage';

export type StorageUploadResult = {
  fullPath: string;
  downloadURL: string;
  name: string;
  size: number;
  contentType?: string;
};

export const collection = {
  users: 'Users',
  quizzes: 'Quizzes',
  quizpackages: 'QuizPackages',
  liveassignments: 'LiveAssignments',
  liveassignmentparticipants: 'LiveAssignmentParticipants',
  quizresponses: 'QuizResponses',
  groups: 'Groups',
  groupMemberships: 'GroupMemberships',
  subjects: 'Subjects',
  lessons: 'Lessons',
  generationProcesses: 'GenerationProcesses',
  fields: 'Fields',
  topics: 'Topics',
  subtopics: 'Subtopics',
  studentSubjectStatistics: 'StudentSubjectStatistics',
  selfAssignments: 'SelfAssignments',
  neptunExports: 'NeptunExports',
  allowedEmailsWithoutNeptun: 'AllowedEmailsWithoutNeptun',
  externalLinks: 'ExternalLinks',
  earnedBadges: 'EarnedBadges',
  challengeSessions: 'ChallengeSessions',
  systemLog: 'SystemLog',
  knowledgeElement: 'KnowledgeElements',
  mail: 'mail',
  learningtasks: 'LearningTasks',
  learningTaskConfigurations: 'LearningTaskConfigurations',
  classworkSessions: 'ClassworkSessions',
  statistics: 'Statistics',
  systemConfig: 'SystemConfig'
};

const archiveAwareCollections = new Set<string>([
  collection.users,
  collection.quizzes,
  collection.quizpackages,
  collection.quizresponses,
  collection.groups,
  collection.groupMemberships,
  collection.subjects,
  collection.lessons,
  collection.generationProcesses,
  collection.fields,
  collection.topics,
  collection.subtopics,
  collection.knowledgeElement,
  collection.learningtasks,
  collection.learningTaskConfigurations,
  collection.classworkSessions,
]);

@Injectable({
  providedIn: 'root',
})
export class FBaseService {
  constructor(
    private afs: AngularFirestore,
    private logger: LoggerService,
    private storage: AngularFireStorage
  ) { }

  private filterArchived<T>(items: T[]): T[] {
    return (items || []).filter((item: any) => item?.archived !== true);
  }

  private filterArchivedDocument<T>(item: T | undefined): T | null {
    return (item as any)?.archived === true ? null : item ?? null;
  }

  private buildArchiveUpdate() {
    return {
      archived: true,
      'metadata.lastUpdatedAt': new Date().toISOString(),
      'metadata.lastUpdatedBy.id': this.logger.getUserId()
    };
  }

  private applyActiveDefault(collectionName: string, data: any, partialUpdate = false): any {
    if (!partialUpdate && archiveAwareCollections.has(collectionName) && data?.archived === undefined) {
      data.archived = false;
    }
    return data;
  }

  get(collectionName: string, limit?: any, orderBy?: OrderBy, startAt?: any, field?: string, fieldPath = 'parentId', opStr = '=='): Observable<any> {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (field) {
          query = query.where(fieldPath, opStr as any, field);
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (orderBy?.active && orderBy?.direction) {
          query = query.orderBy(orderBy.active, orderBy.direction);
        } else {
          query = query.orderBy('id');
        }
        if (startAt) {
          query = query.startAt(startAt[orderBy?.active ? orderBy.active : 'id']);
        }
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  getByMultiField(collectionName: string, limit = 24, orderBy?: OrderBy[],
    startAt?: any, queryParam?: QueryParam[]): Observable<any> {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (queryParam) {
          queryParam.forEach(e => {
            if (e.field && e.opStr && e.value !== null) {
              query = query.where(e.field, e.opStr as any, e.value);
            }
          });
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (orderBy?.length) {
          orderBy.forEach((orderElement: OrderBy) => {
            query = query.orderBy(orderElement.active, orderElement.direction ? orderElement.direction : undefined);
          });
        } else {
          query = query.orderBy('id');
        }
        if (startAt && orderBy) {
          query = query.startAt(...orderBy?.map(item => getAttributeByPath(startAt, item.active.split('.'))));
        }
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  getByMultiField2(
    collectionName: string,
    limit: number | null = 24,
    orderBy?: OrderBy | OrderBy[],
    startAt?: any,
    queryParam?: QueryParam[]
  ) {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (queryParam) {
          queryParam.forEach(e => {
            if (e.field && e.opStr && e.value != null) {
              query = query.where(e.field, e.opStr as any, e.value);
            }
          });
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (Array.isArray(orderBy)) {
          orderBy.forEach(o => {
            if (o.active && o.direction) {
              query = query.orderBy(o.active, o.direction);
            }
          });
        } else {
          if (orderBy?.active && orderBy?.direction) {
            query = query.orderBy(orderBy.active, orderBy.direction);
          } else {
            query = query.orderBy('id');
          }
        }
        if (startAt && Array.isArray(orderBy)) {
          query = query.startAt(...this.getStartAtByOrderBy(startAt, orderBy));
        } else if (startAt && !Array.isArray(orderBy)) {
          query = query.startAt(startAt[orderBy?.active ? orderBy.active : 'id']);
        }
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  private getStartAtByOrderBy(data: any, orderBy: OrderBy[]): any[] {
    const valuePath = orderBy
      .filter(item => item.active && item.direction)
      .map(order => order.active!.split('.'));
    return getResultValues(data, valuePath);
  }

  getById(collectionName: string, id: string): Observable<any> {
    return this.afs.collection(collectionName).doc(id).valueChanges()
      .pipe(map(item => this.filterArchivedDocument(item)));
  }

  getByName(collectionName: string, name: string, limit: number = 1, fieldValue: string = 'name', opStr = 'array-contains', org?: string | number | boolean, parentPath = 'parentId'): Observable<any> {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (org) {
          query = query.where(parentPath, '==', org);
        }
        if (name) {
          query = query.where(fieldValue, opStr as any, name);
        }
        query = query.limit(limit);
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  getByAuto(
    collectionName: string,
    name: string | string[],
    limit: number = 1,
    fieldPath: string = 'name',
    opStr = 'array-contains',
    parentPath?: string,
    org?: string | string[] | boolean,
    parentOpStr = '==',
    /* extraForOrg = false,
    type?: string,
    active = true */
  ) {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (parentPath && org) {
          query = query.where(parentPath, parentOpStr as any, org);
        }
        if (name) {
          query = query.where(fieldPath, opStr as any, name);
        }
        /* if (extraForOrg) {
          query = query.where('type', '==', type);
          query = query.where('active', '==', active);
        } */
        /* query = query.orderBy(fieldPath, 'asc'); */
        query = query.limit(limit);
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  searchByValue(collection: string, field: string, value: string, limit: number, org?: string): Observable<any> {
    return this.afs
      .collection(collection, ref => {
        let query: CollectionReference | Query = ref;
        if (org) {
          query = query.where('parentId', '==', org);
        }
        query = query.orderBy(field);
        query = query.startAt(value);
        query = query.limit(limit);
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  /** @deprecated Use 'saveOrUpdateData(collection: string, data: any)' instead */
  async add(collectionName: string, data: any, id?: string) {
    const uid = id ? id : this.afs.createId();
    data.id = uid;
    this.applyActiveDefault(collectionName, data);
    await this.afs.collection(collectionName).doc(uid).set(data);
    return uid;
  }

  async countDataByField(collection: string, wh: { fieldPath: string; value: any; opStr: any }[] = []): Promise<number> {
    const filters = [];
    for (const w of wh) {
      filters.push(where(w.fieldPath, w.opStr, w.value));
    }
    return (await getCountFromServer(query(coll(this.afs.firestore, collection), ...filters))).data().count;
  }

  getDataByField(collection: string, where: { fieldPath: string; value: any; opStr: any }[] = [], orderBy?: OrderBy, limit?: number, startAfter?: any, noOrder: boolean = false): Observable<any[]> {
    // console.log(collection, where, orderBy, limit, startAfter, noOrder);
    return this.afs
      .collection(collection, ref => {
        let query: CollectionReference | Query = ref;
        for (const w of where) {
          query = query.where(w.fieldPath, w.opStr, w.value);
        }
        if (orderBy && orderBy.active && orderBy.direction) {
          query = query.orderBy(orderBy.active, orderBy.direction);
        } else {
          if (!noOrder) {
            query = query.orderBy('metadata.createdAt', 'desc');
          }
        }
        if (startAfter) {
          query = query.startAfter(startAfter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  getDataByField2(collection: string, where: { fieldPath: string; value: any; opStr: any }[] = [], orderBy?: OrderBy[], limit?: number, startAt?: string[]): Observable<any[]> {
    return this.afs
      .collection(collection, ref => {
        let query: CollectionReference | Query = ref;
        for (const w of where) {
          query = query.where(w.fieldPath, w.opStr, w.value);
        }
        if (orderBy?.length) {
          for (const o of orderBy) {
            query = query.orderBy(o.active, o.direction);
          }
        }
        else {
          query = query.orderBy('metadata.createdAt', 'desc');
        }
        if (startAt && startAt.length) {
          query = query.startAt(...startAt);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query;
      })
      .valueChanges()
      .pipe(map(items => this.filterArchived(items as any[])));
  }

  async getDataByFieldPromise(collection: string, where: { fieldPath: string; value: any; opStr: any }[] = [], orderBy?: OrderBy, limit?: number, startAfter?: any, noOrder: boolean = false): Promise<any[]> {
    // console.log(collection, where, orderBy, limit, startAfter, noOrder);
    const snapshot = await firstValueFrom(this.afs
      .collection(collection, ref => {
        let query: CollectionReference | Query = ref;
        for (const w of where) {
          query = query.where(w.fieldPath, w.opStr, w.value);
        }
        if (orderBy && orderBy.active && orderBy.direction) {
          query = query.orderBy(orderBy.active, orderBy.direction);
        } else {
          if (!noOrder) {
            query = query.orderBy('metadata.createdAt', 'desc');
          }
        }
        if (startAfter) {
          query = query.startAfter(startAfter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query;
      }).get());
    return this.filterArchived(snapshot.docs.map(doc => doc.data()));
  }

  getDataByFieldSnapshot(collection: string, fieldPath: string, opStr: any, value: any): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const data: any[] = [];
      this.afs
        .collection(collection)
        .ref.where(fieldPath, opStr, value)
        .get()
        .then(snapshot => {
          snapshot.forEach(result => {
            const item = result.data();
            if ((item as any)?.archived !== true) {
              data.push(item);
            }
          });
          resolve(data);
          return;
        })
        .catch(error => {
          reject(error);
          return;
        });
    });
  }

  saveOrUpdateData(collection: string, data: any, partialUpdate: boolean = false): Promise<any> {
    return new Promise((resolve, reject) => {
      if (collection && data) {
        if (!data.id) {
          data.id = this.afs.createId();
        }
        if (!data.metadata) {
          data.metadata = {};
        }
        if (!data.metadata.createdBy) {
          data.metadata.createdBy = {};
        }
        if (!data.metadata.createdBy.id) {
          data.metadata.createdBy.id = this.logger.getUserId();
        }
        if (!data.metadata.createdAt) {
          data.metadata.createdAt = new Date().toISOString();
        }
        if (!data.metadata.lastUpdatedBy) {
          data.metadata.lastUpdatedBy = {};
        }
        // Always update lastUpdated at and by
        data.metadata.lastUpdatedAt = new Date().toISOString();
        data.metadata.lastUpdatedBy.id = this.logger.getUserId();
        this.applyActiveDefault(collection, data, partialUpdate);
        this.afs
          .collection(collection)
          .doc(data.id)
          .set(data, { merge: partialUpdate ? true : false })
          .then(() => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        reject();
      }
    });
  }

  getUid() {
    return this.afs.createId();
  }

  updateEducationIdOnly(id: string, newEducationId: string) {
    return this.afs.collection(collection.groupMemberships).doc(id).update({
      'studentData.educationId': newEducationId,
      'metadata.lastUpdatedAt': new Date().toISOString(),
      'metadata.lastUpdatedBy.id': this.logger.getUserId(),
    });
  }

  /** @deprecated Use 'saveOrUpdateData(collection: string, data: any)' instead */
  set(collectionName: string, id: string, data: any) {
    this.applyActiveDefault(collectionName, data);
    return this.afs.collection(collectionName).doc(id).set(data);
  }

  /** @deprecated Use 'saveOrUpdateData(collection: string, data: any)' instead */
  update(collectionName: string, id: string, data: any) {
    return this.afs.collection(collectionName).doc(id).update(data);
  }

  delete(collectionName: string, id: string) {
    return this.afs.collection(collectionName).doc(id).update(this.buildArchiveUpdate());
  }

  async uploadFile(
    file: File,
    opts?: {
      folder?: string; // default: "uploads"
      fileName?: string; // default: file.name
      metadata?: Record<string, string>;
    }
  ): Promise<string> {
    if (!file) throw new Error('No file provided');

    const folder = opts?.folder ?? 'uploads';
    const fullPath = `${folder}/${this.generateRandomId()}`;

    let retries = 0;
    while (retries < 5) {
      try {
        const task = this.storage.upload(fullPath, file, {
          contentType: file.type || 'application/octet-stream',
          customMetadata: opts?.metadata,
        });
        await task;
        break;
      } catch (error) {
        retries++;
        if (retries >= 5) {
          throw new Error("UPLOAD_ERROR");
        }
        await new Promise(r => setTimeout(r, 300 * retries));
      }
    }
    return fullPath;
  }

  generateRandomId(length = 16): string {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const values = new Uint32Array(length);
    crypto.getRandomValues(values);

    return Array.from(values, v => chars[v % chars.length]).join('');
  }

  async getFile(path: string) {
    const downloadURL = await firstValueFrom(this.storage.ref(path).getDownloadURL());
    return downloadURL;
  }
}
